import { Box, Flex, Icon, Image, Text } from "@chakra-ui/react";
import { MdPlayArrow } from "react-icons/md";
import { IoHourglassOutline } from "react-icons/io5";
import { OptimizeGame } from "../../data";
import { CoinIcon, DiamondIcon, PlayStoreIcon, RewardBoxIcon } from "./MLottie";

// Web replicas of the PlayMAX mobile UI (mobile/playmax, components/screens/rework).
// Values come from constants/colors.ts + fonts.ts and the components' own StyleSheets,
// computed at deviceHeight=852 / deviceWidth=393 (iPhone 14 Pro). The rework screens
// size everything off raw `deviceHeight / N`, so the fractional values below are exact.
//
// Fonts: PRIMARY_FONT_* is Quicksand (Regular 400 / Medium 500 / Bold 700) and is used
// for nearly all text; THIRD_FONT_* is Montserrat and only shows up on the boosted
// reward numbers and the double-rewards banner.

export const TM = {
  font: "'Quicksand', sans-serif",
  fontAlt: "'Montserrat', sans-serif",
  bg: "#f2f2f7",
  card: "#fff",
  text: "#282d59",
  textSecondary: "#515577",
  primary: "rgb(21,209,131)",
  secondary: "#edac4b",
  inactive: "#8f91aa",
  green: "#22c55e",
  greenTrack: "#22c55e1F",
  activeBlue: "#50a0ff",
  ctaGradient: "linear-gradient(90deg, #15D183, #1dd89f, #15D183)",
  claimGradient: "linear-gradient(90deg, #F2003C, #ff1a4d, #F2003C)",
  cardGradient: "linear-gradient(135deg, #ffffff 0%, #fafbff 50%, #f5f7ff 100%)",
  cardBorder: "rgba(182,157,255,0.3)",
  red: "#F2003C",
  coin: "#f59e0b",
  gem: "#00A0FF",
};

// screen-colored surface the in-app preview sits on (list padding = deviceHeight/50)
export const PreviewSurface: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  // only carries the app's font: no background or padding, so the app card sits
  // directly under the title instead of inside a second surface
  <Box fontFamily={TM.font} h="100%" sx={{ "& *": { fontFamily: "inherit" } }}>
    {children}
  </Box>
);

export const MGameIcon: React.FC<{
  game?: OptimizeGame | null;
  size?: number | string;
  radius?: string;
}> = ({ game, size = 44, radius = "14.2px" }) => {
  const dim = typeof size === "number" ? `${size}px` : size;
  return (
    <Flex
      w={dim}
      h={dim}
      borderRadius={radius}
      // placeholder tiles use REWORK_COLORS.secondary in GameRow/PlayGamesRow
      background={game ? game.gradient : TM.secondary}
      align="center"
      justify="center"
      flexShrink={0}
      overflow="hidden"
    >
      {game?.icon && (
        <Image
          src={game.icon}
          alt=""
          w="100%"
          h="100%"
          objectFit="cover"
          // store artwork that 404s would otherwise leave a broken-image glyph
          // and the title's name sitting in the tile; fall through to the gradient
          fallback={<Box w="100%" h="100%" />}
        />
      )}
    </Flex>
  );
};

// green pill CTA, or the red "Claim" variant (TaskCard.tsx ctaButton* / :592-606)
export const MPlayButton: React.FC<{ label?: string }> = ({
  label = "PLAY",
}) => {
  const claim = label.toLowerCase() === "claim";
  return (
    <Flex
      align="center"
      justify="center"
      background={claim ? TM.claimGradient : TM.ctaGradient}
      borderRadius="10.65px"
      border="1px solid rgba(255,255,255,0.25)"
      boxShadow={
        claim
          ? "0 5px 12px rgba(242,0,60,0.5)"
          : "0 5px 12px rgba(21,209,131,0.5)"
      }
      px="17px"
      py="8.5px"
      minH="34px"
      minW="61px"
      ml="8.5px"
      mr="-4.26px"
      flexShrink={0}
    >
      <Text
        color="white"
        fontWeight="700"
        fontSize="14.2px"
        letterSpacing="0.4px"
        textShadow="1px 1.5px 3px rgba(0,0,0,0.35)"
      >
        {label}
      </Text>
    </Flex>
  );
};

// "NEW" badge that overhangs the top-left corner. TaskCard.tsx :833-861 uses a
// gradient + red glow; GameRow.tsx :263-276 uses flat red with no shadow.
export const MNewBadge: React.FC<{ variant?: "task" | "tile" }> = ({
  variant = "task",
}) => (
  <Flex
    position="absolute"
    left="-7.1px"
    top="-7.1px"
    background={
      variant === "task"
        ? "linear-gradient(135deg, #F2003C 0%, #ff1a4d 100%)"
        : TM.red
    }
    borderRadius="4.26px"
    px="4.26px"
    py="2.84px"
    boxShadow={variant === "task" ? "0 3px 5px rgba(242,0,60,0.5)" : undefined}
    zIndex={2}
  >
    <Text
      color="white"
      fontWeight="500"
      fontSize="9.5px"
      textShadow={
        variant === "task" ? "0 1px 2px rgba(0,0,0,0.2)" : undefined
      }
    >
      NEW
    </Text>
  </Flex>
);

// circular play/loading bubble on an app icon's top-right. TaskCard.tsx :514-540
// (2.5px border, 12.17px glyph) vs GameRow.tsx :277-294 (2px border, 14.2px glyph).
export const MStatusBubble: React.FC<{
  loading?: boolean;
  variant?: "task" | "tile";
}> = ({ loading, variant = "task" }) => {
  const glyph = variant === "task" ? "12.17px" : "14.2px";
  return (
    <Flex
      position="absolute"
      top="-7.1px"
      right="-7.1px"
      w="24.34px"
      h="24.34px"
      borderRadius="12.17px"
      bg={loading ? TM.activeBlue : TM.green}
      border={`${variant === "task" ? 2.5 : 2}px solid #fff`}
      boxShadow={
        variant === "task" ? "0 2px 3px rgba(0,0,0,0.25)" : undefined
      }
      align="center"
      justify="center"
      zIndex={2}
    >
      {!loading && (
        <Icon
          as={MdPlayArrow}
          color="white"
          w={glyph}
          h={glyph}
          position="relative"
          right="-0.85px"
        />
      )}
    </Flex>
  );
};

// Task card shell: full-height app icon flush-left + title + CTA (TaskCard.tsx
// "default"). Top-right corner is square; the icon only rounds its left corners.
// Fills its cell by default so cards on the same dashboard row read as equal
// height; `fill={false}` keeps the natural height — Günay asked only the top
// row's Recurring / Rewarded Ad cards to drop their stretched empty space.
export const MTaskCard: React.FC<{
  game?: OptimizeGame | null;
  title?: string;
  subtitle?: string;
  cta?: string;
  isNew?: boolean;
  showStatus?: boolean;
  fill?: boolean;
  header?: React.ReactNode;
  children?: React.ReactNode;
}> = ({
  game,
  title,
  subtitle,
  cta = "PLAY",
  isNew,
  showStatus,
  fill = true,
  header,
  children,
}) => {
  return (
    // the app spaces stacked task cards apart; the dashboard preview shows one
    // card at a time, so the trailing gap is dead space in the grid
    <Flex direction="column" h={fill ? "100%" : undefined}>
      {header}
      <Box
        position="relative"
        {...(fill
          ? { flex: "1", display: "flex", flexDirection: "column" }
          : {})}
      >
        {isNew && <MNewBadge />}
        <Box
          background={TM.cardGradient}
          border={`0.5px solid ${TM.cardBorder}`}
          borderRadius="8.52px"
          position="relative"
          flex={fill ? "1" : undefined}
          // the glow lines below are 2px tall, so they can't honour the card's
          // radius on their own — clip them instead of letting them square off
          // the rounded corners
          overflow="hidden"
        >
          {/* cardInnerGlow / cardBottomGlow (TaskCard.tsx :712-729) */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            h="2px"
            bg="#fff"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            h="1px"
            bg="rgba(182,157,255,0.15)"
            pointerEvents="none"
          />
          {/* The app runs the icon flush into the card's left edge; here it is
              inset by the card's own 14.2px padding and fully rounded, which
              leaves it at 56.8px — the app's own tile size (deviceHeight/15). */}
          <Flex align="center" minH="72px" px="12px" py="10px">
            <Box
              position="relative"
              flexShrink={0}
              boxShadow="0 2px 6px rgba(0,0,0,0.12)"
              borderRadius="14.2px"
            >
              <MGameIcon game={game} size={56.8} radius="14.2px" />
              {showStatus && <MStatusBubble />}
            </Box>
            <Flex
              flex="1"
              direction="column"
              justify="center"
              ml="10.65px"
              pr="8.52px"
              minW="0"
            >
              <Text
                color={TM.text}
                fontWeight="700"
                fontSize="14.7px"
                lineHeight="18.93px"
                textShadow="0 1px 2px rgba(0,0,0,0.02)"
                noOfLines={2}
              >
                {title ?? game?.name ?? "Game"}
              </Text>
              {subtitle && (
                <Text
                  color={TM.text}
                  fontSize="11.5px"
                  opacity={0.85}
                  mt="2.84px"
                  noOfLines={1}
                >
                  {subtitle}
                </Text>
              )}
            </Flex>
            <Flex align="center">
              <MPlayButton label={cta} />
            </Flex>
          </Flex>
          {children && (
            <Box px="12px" pb="8px">
              {children}
            </Box>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

// thin bar + centered caption (Bonus/ProgressBar.tsx + Watch GameTaskRow label)
export const MProgress: React.FC<{
  value: number; // 0..100
  label?: string;
  title?: string;
  height?: string;
  rewardChip?: React.ReactNode;
  leading?: React.ReactNode; // sits left of the title (per-row reward pill)
}> = ({ value, label, title, height = "10.65px", rewardChip, leading }) => {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <Box mb="6px">
      {(title || rewardChip || leading) && (
        <Flex align="center" justify="space-between" gap="5.68px" mb="2px">
          <Flex align="center" gap="5.68px" minW="0">
            {leading}
            {title && (
              <Text color={TM.text} fontWeight="500" fontSize="13.1px">
                {title}
              </Text>
            )}
          </Flex>
          {rewardChip}
        </Flex>
      )}
      <Box
        bg={TM.greenTrack}
        borderRadius="4.26px"
        h={height}
        mt="4px"
        overflow="hidden"
      >
        {/* ProgressBar.tsx:23 — 5% minimum so an empty bar still reads as a bar */}
        <Box
          bg={TM.green}
          borderRadius="4.26px"
          h="100%"
          w={`${5 + (pct * 95) / 100}%`}
        />
      </Box>
      {label && (
        <Text
          color={TM.textSecondary}
          fontSize="12.5px"
          fontWeight="500"
          textAlign="center"
          mt="2px"
        >
          {label}
        </Text>
      )}
    </Box>
  );
};

// The reward always sits in a white, top-rounded, fixed-height tab welded to the
// card's top-right corner — which is why the card squares that corner
// (EnhancedCoinDisplay.tsx normalRewardsWrapper :198-209).
const MRewardTab: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex
    h="42.6px"
    w="fit-content"
    bg={TM.card}
    borderTopRadius="7.1px"
    align="center"
    justify="center"
    px="5.68px"
    flexShrink={0}
  >
    {/* normalRewardItem (EnhancedCoinDisplay.tsx :217-219) — the wrapper's own
        gap never applies in the app, it has this single child */}
    <Flex align="center" mx="4.26px">
      {children}
    </Flex>
  </Flex>
);

// coin / gem amount chip (EnhancedCoinDisplay non-boosted branch :38-101)
export const MReward: React.FC<{
  amount: string;
  kind?: "coin" | "gem";
  withBox?: boolean;
}> = ({ amount, kind = "coin", withBox }) => (
  <MRewardTab>
    {withBox && (
      <Box mt="-10.65px" mb="-10.65px" ml="-4.73px" mr="4.26px">
        <RewardBoxIcon size={47} />
      </Box>
    )}
    <Flex align="center" flexShrink={0}>
      <Text color={TM.text} fontWeight="700" fontSize="18.9px">
        {amount}
      </Text>
      {kind === "coin" ? <CoinIcon size={24} /> : <DiamondIcon size={19} />}
    </Flex>
  </MRewardTab>
);

// boosted reward tab: white top-rounded strip holding the new amount, the
// struck-through old one, then a single coin (EnhancedCoinDisplay.tsx :108-219)
export const MBoostedReward: React.FC<{
  original: number;
  boosted: number;
  gems?: number;
}> = ({ original, boosted, gems }) => (
  <MRewardTab>
    <Box mt="-8.52px" mb="-8.52px" ml="-4.26px" mr="5.68px">
      <RewardBoxIcon size={35} />
    </Box>
    {/* amounts and the gems line stack in a column INSIDE the tab, so the tab
        stays 42.6px and stays flush on the card (EnhancedCoinDisplay.tsx :121-191) */}
    <Flex direction="column">
      <Flex align="center">
        <Text
          color={TM.red}
          fontFamily={TM.fontAlt}
          fontWeight="600"
          fontStyle="italic"
          fontSize="15.5px"
        >
          {boosted}
        </Text>
        <Flex align="center" position="relative" ml="2.84px">
          <Text
            color="#515577c0"
            fontFamily={TM.fontAlt}
            fontWeight="600"
            fontStyle="italic"
            fontSize="14.2px"
            opacity={0.6}
          >
            {original}
          </Text>
          <Box as="svg" position="absolute" top="0" left="-5px" w="calc(100% + 5px)" h="100%">
            <line
              x1="0"
              y1="100%"
              x2="100%"
              y2="2"
              stroke={TM.red}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </Box>
        </Flex>
        <Box position="relative" top="-1.42px">
          <CoinIcon size={24} />
        </Box>
      </Flex>
      {gems !== undefined && (
        <Flex
          align="center"
          justify="center"
          alignSelf="center"
          position="relative"
          top="-2.13px"
        >
          <Text color={TM.text} fontWeight="700" fontSize="10.65px">
            +{gems}
          </Text>
          <Box position="relative" top="-2.13px">
            <DiamondIcon size={15} />
          </Box>
        </Flex>
      )}
    </Flex>
  </MRewardTab>
);

// red HH:MM:SS line, no label (TimeLimitedCountdown.tsx :52-70)
export const MCountdown: React.FC<{ time: string }> = ({ time }) => (
  <Text
    color={TM.red}
    fontWeight="600"
    fontSize="17px"
    textAlign="center"
    mb="5.68px"
  >
    {time}
  </Text>
);

// white section card (Bonus/SectionCard.tsx :210-257) — no shadow in the app.
// Fills its cell so cards on the same dashboard row read as equal height;
// `fill` additionally makes it a flex column so a card can centre its content
// in the leftover height.
export const MSectionCard: React.FC<{
  title?: string;
  fill?: boolean;
  children: React.ReactNode;
}> = ({ title, fill, children }) => (
  <Box
    bg={TM.card}
    borderRadius="14px"
    p="10px"
    overflow="hidden"
    h="100%"
    {...(fill ? { display: "flex", flexDirection: "column" } : {})}
  >
    {title && (
      <Text
        color={TM.text}
        fontWeight="700"
        fontSize="15.5px"
        textAlign="center"
        pb="8px"
      >
        {title}
      </Text>
    )}
    {children}
  </Box>
);

// collectable stepper: zig-zag row of themed items joined by diagonal connectors,
// each in a tinted circular chip (Live/components/RewardStepper/index.tsx), like
// the app's own two-row layout. Up to 7 chips fit the card whole; longer strips
// scroll, with the viewport capped so it cuts mid-chip — a visible half
// collectible is what tells the partner the strip scrolls (Günay's feedback).
export const MCollectables: React.FC<{
  emoji: string;
  total: number;
  collected: number;
  accent?: string;
}> = ({ emoji, total, collected, accent = TM.green }) => {
  // ITEM_SIZE / ITEM_SPACING = deviceWidth*0.10, VERTICAL_OFFSET = deviceWidth*0.05
  const ITEM = 39.3;
  const RADIUS = ITEM / 2;
  const SPACING = 39.3;
  const OFFSET = 19.65;
  const trackH = ITEM + OFFSET * 2;
  const cx = (i: number) => i * SPACING + RADIUS;
  const cy = (i: number) => (i % 2 === 0 ? 0 : OFFSET * 2) + RADIUS;
  const FIT = 7;
  return (
    <Box
      overflowX="auto"
      mb="8px"
      mx="auto"
      maxW={total > FIT ? `${(FIT + 0.5) * SPACING}px` : undefined}
      sx={{ "&::-webkit-scrollbar": { display: "none" } }}
    >
      <Box
        position="relative"
        h={`${trackH}px`}
        w={`${(total - 1) * SPACING + ITEM}px`}
        mx="auto"
      >
        {Array.from({ length: total - 1 }, (_, i) => {
          // connector spans only the gap between the two chips, rotated about
          // its own centre on the midpoint (RewardStepper getConnectorStyle)
          const dx = cx(i + 1) - cx(i);
          const dy = cy(i + 1) - cy(i);
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const len = Math.max(dist - RADIUS * 2, 0);
          const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
          return (
            <Box
              key={`line-${i}`}
              position="absolute"
              left={`${(cx(i) + cx(i + 1)) / 2 - len / 2}px`}
              top={`${(cy(i) + cy(i + 1)) / 2 - 1}px`}
              w={`${len}px`}
              h="2px"
              bg={i + 1 < collected ? accent : `${accent}40`}
              transform={`rotate(${angle}deg)`}
            />
          );
        })}
        {Array.from({ length: total }, (_, i) => {
          const filled = i < collected;
          return (
            <Flex
              key={i}
              position="absolute"
              left={`${i * SPACING}px`}
              top={`${i % 2 === 0 ? 0 : OFFSET * 2}px`}
              w={`${ITEM}px`}
              h={`${ITEM}px`}
              align="center"
              justify="center"
            >
              <Flex
                w={`${ITEM - 8}px`}
                h={`${ITEM - 8}px`}
                borderRadius="99px"
                bg={filled ? `${accent}33` : `${accent}1A`}
                align="center"
                justify="center"
              >
                <Text fontSize="18px" sx={filled ? {} : { opacity: 0.4 }}>
                  {emoji}
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
};

// game tile grid: 71px icons, 2-line labels, NEW badge + status bubble, optional
// per-tile progress bar (Bonus/GameRow.tsx + PlayGamesRow.tsx)
export const MGameTiles: React.FC<{
  games: (OptimizeGame | null)[];
  progress?: (number | undefined)[];
  newIndex?: number;
  statusIndex?: number;
}> = ({ games, progress, newIndex, statusIndex }) => (
  <Flex align="flex-start" justify="space-between" gap="10.65px" mb="4px">
    {games.map((game, i) => (
      <Flex
        key={i}
        direction="column"
        align="center"
        justify="space-between"
        flex="1"
        minW="0"
      >
        <Box position="relative">
          <MGameIcon game={game} size={64} radius="13px" />
          {newIndex === i && <MNewBadge variant="tile" />}
          {statusIndex === i && <MStatusBubble variant="tile" />}
        </Box>
        <Text
          color={TM.text}
          fontWeight="500"
          fontSize="13px"
          lineHeight="14.2px"
          minH="28.4px"
          mt="5px"
          mb="4px"
          textAlign="center"
          noOfLines={2}
        >
          {game ? game.name : "Game"}
        </Text>
        {progress?.[i] !== undefined && (
          <Box w="100%">
            <MProgress value={progress[i]!} height="8.52px" />
          </Box>
        )}
      </Flex>
    ))}
  </Flex>
);

// segmented completion bar under a game row (Bonus/SegmentBar.tsx)
export const MSegmentBar: React.FC<{ total: number; filled: number }> = ({
  total,
  filled,
}) => (
  <Flex gap="4.26px" mb="8px">
    {Array.from({ length: total }, (_, i) => (
      <Box
        key={i}
        flex="1"
        h="7.1px"
        borderRadius="4.26px"
        bg={i < filled ? TM.green : TM.greenTrack}
      />
    ))}
  </Flex>
);

// app row inside a collectable event: reward pill beside the CTA, in one card
// (Live/.../PlaytimeEvent/PlaytimeAppRow.tsx)
export const MCollectableRow: React.FC<{
  game?: OptimizeGame | null;
  appName: string;
  task: string;
  count?: number;
  emoji: string;
  accent?: string;
  cta?: string;
}> = ({
  game,
  appName,
  task,
  count = 1,
  emoji,
  accent = TM.green,
  cta = "PLAY",
}) => (
  <Box mb="8px">
    <Flex
      align="center"
      justify="space-between"
      gap="8.5px"
      bg={TM.card}
      borderRadius="8.5px"
      p="7px"
      boxShadow="0 1px 2px rgba(0,0,0,0.1)"
    >
      <Flex align="center" gap="8.5px" flex="1" minW="0">
        <MGameIcon game={game} size={38} radius="9px" />
        <Box minW="0">
          <Text color={TM.text} fontWeight="700" fontSize="12.8px" noOfLines={1}>
            {appName}
          </Text>
          <Text color={TM.textSecondary} fontWeight="400" fontSize="11.1px">
            {task}
          </Text>
        </Box>
      </Flex>
      <Flex align="center" gap="6.4px" flexShrink={0}>
        <Flex align="center" gap="2.13px">
          <Text color={TM.text} fontWeight="700" fontSize="15.3px">
            {count}
          </Text>
          <Text fontSize="15.3px">{emoji}</Text>
        </Flex>
        <Flex
          align="center"
          justify="center"
          px="14.9px"
          py="6.4px"
          borderRadius="21.3px"
          border={`2px solid ${accent}`}
        >
          <Text color={accent} fontWeight="700" fontSize="11.9px">
            {cta}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  </Box>
);

// hourglass tag: icon first, then the label, with an optional red time-limit chip
// (SectionCard.tsx :56-69, :226-241, :302-312)
export const MDurationTag: React.FC<{
  label: string;
  timeLimit?: string;
}> = ({ label, timeLimit }) => (
  <Flex align="center" gap="5.68px" flexShrink={0}>
    <Icon
      as={IoHourglassOutline}
      color={TM.textSecondary}
      w="14.2px"
      h="14.2px"
    />
    <Text
      color={TM.textSecondary}
      fontWeight="500"
      fontSize="13.1px"
      position="relative"
      top="-0.85px"
    >
      {label}
    </Text>
    {timeLimit && (
      <Box px="5.68px" borderLeft={`0.75px solid ${TM.textSecondary}`}>
        <Text color={TM.red} fontWeight="700" fontSize="13.1px">
          {timeLimit}
        </Text>
      </Box>
    )}
  </Flex>
);

// per-row coin reward pill — the boxed amounts beside each retention streak row
export const MCoinChip: React.FC<{ amount: string }> = ({ amount }) => (
  <Flex
    align="center"
    gap="2.84px"
    px="5.68px"
    h="21.3px"
    borderRadius="5.68px"
    border={`0.75px solid ${TM.greenTrack}`}
    bg={TM.bg}
    flexShrink={0}
  >
    <CoinIcon size={14.2} />
    <Text color={TM.text} fontWeight="700" fontSize="12.5px">
      {amount}
    </Text>
  </Flex>
);

// ─── Playtime "New" tab offer card ──────────────────────────────────────────
// Replica of playtime_android's DisplayedOffer (components/screens/Offers/
// components/Offers/DisplayedOffer) — the card Playtime and MoneyCash show on
// their New tab. Same reference device as above (852 × 393); the card's own
// width there is `offerWidth = deviceWidth / 1.1 = 357.27`.
//
// The reward tab (TOTAL REWARD $x) and the coins/min "play until" strip are
// deliberately left out: the partner-facing preview must not put reward figures
// in front of a partner.

const OFFER_W = 357.27;
const OFFER_BANNER_RATIO = OFFER_W / (852 * 0.229); // banner is 0.229 * height

// An app pixel value that holds at its real size while the preview column is at
// least as wide as the app card, and shrinks with the column below that — so the
// whole replica scales as one piece instead of a 85px play button swallowing a
// narrow card. Needs `containerType: inline-size` on the card (set below).
const s = (px: number) => {
  const cqw = `${((px / OFFER_W) * 100).toFixed(3)}cqw`;
  // min() picks the more negative of two negatives, which is backwards for
  // offsets like the ribbon's overhang
  return px < 0 ? `max(${px}px, ${cqw})` : `min(${px}px, ${cqw})`;
};

// Diagonal corner ribbon (OfferBadge.tsx). Its SVG gradient axis is (0,1) →
// (1,0) across the 119 × 30 rect, i.e. 75.8deg in CSS terms.
const MOfferRibbon: React.FC<{ label?: string }> = ({ label = "TOP" }) => (
  <Flex
    position="absolute"
    zIndex={1}
    top={s(7.77)}
    right={s(-22.33)}
    w={s(119.09)}
    h={s(29.77)}
    transform="rotate(35deg)"
    align="center"
    justify="center"
    background="linear-gradient(75.8deg, #5500ff 0%, #712cfd 60%, #501eb6 100%)"
    boxShadow="0 1px 3px rgba(0,0,0,0.25)"
  >
    <Text
      color="white"
      fontWeight="700"
      fontSize={s(label === "TOP" ? 19.85 : 11.91)}
      lineHeight="1"
      position="relative"
      top={s(-1.19)}
      left={s(label === "TOP" ? 3.57 : 5.95)}
      whiteSpace="nowrap"
    >
      {label}
    </Text>
  </Flex>
);

// OfferPlayVideoButton.tsx — deviceHeight/10 circle, 5px white ring, 50% black
const MOfferPlayButton: React.FC = () => (
  <Flex
    w={s(85.2)}
    h={s(85.2)}
    borderRadius="full"
    border={`${s(5)} solid rgba(255,255,255,0.9)`}
    bg="rgba(0,0,0,0.5)"
    align="center"
    justify="center"
    flexShrink={0}
  >
    <Icon as={MdPlayArrow} color="rgba(255,255,255,0.9)" w={s(56.8)} h={s(56.8)} />
  </Flex>
);

// what fills the creative slot, resolved by the caller per the override →
// store-default chain (CREATIVES_FRONTEND_GUIDE.md §4); null keeps the
// gradient placeholder
export type OfferMedia =
  | { kind: "image"; url: string }
  | { kind: "video"; url: string } // a real media file — inline, muted loop
  | { kind: "embed"; url: string }; // a YouTube embed URL — iframe, not <video>

// Nothing dashboard-y is drawn over the creative slot: partners read overlays
// as part of the creative their users would see (Günay's feedback on the old
// language tag) — language switching lives outside this replica.
// The app's subtitle + LiveBadge row ("NEW FOR YOU · 13068 ONLINE") is left
// out per Günay: the preview shows the full store title on one line instead,
// and the shorter card lines up with its row neighbours.
export const MOfferCard: React.FC<{
  game?: OptimizeGame | null;
  // the full store title; falls back to the shortened game name
  title?: string;
  cta?: string;
  ribbon?: string;
  media?: OfferMedia | null;
}> = ({
  game,
  title,
  cta = "PLAY & EARN",
  ribbon = "TOP",
  media,
}) => (
  <Box
    bg="#fff"
    border="1px solid #b3b3b3"
    borderRadius={s(7.1)}
    boxShadow="0 2px 6px rgba(0,0,0,0.16)"
    pb={s(10.65)}
    // the Ring pulse is taller than the CTA it sits behind; Android clips it to
    // the card, and letting it bleed onto the dashboard reads as a glitch
    overflow="hidden"
    sx={{ containerType: "inline-size" }}
  >
    {/* The creative slot: the real creative when the caller resolved one, the
        title's gradient as the nothing-on-file placeholder (an app icon is not
        a landscape asset, so it never stands in for artwork). */}
    <Box
      position="relative"
      overflow="hidden"
      background={game?.gradient ?? TM.secondary}
      sx={{ aspectRatio: `${OFFER_BANNER_RATIO}` }}
    >
      {media?.kind === "image" && (
        <Image
          src={media.url}
          alt=""
          position="absolute"
          inset="0"
          w="100%"
          h="100%"
          objectFit="cover"
        />
      )}
      {media?.kind === "video" && (
        <Box
          as="video"
          key={media.url} // src swaps don't reload a mounted <video>
          src={media.url}
          autoPlay
          muted
          loop
          playsInline
          position="absolute"
          inset="0"
          w="100%"
          h="100%"
          sx={{ objectFit: "cover" }}
        />
      )}
      {media?.kind === "embed" && (
        <Box
          as="iframe"
          key={media.url}
          src={media.url}
          title="Store trailer"
          allow="encrypted-media; picture-in-picture"
          position="absolute"
          inset="0"
          w="100%"
          h="100%"
          border="0"
        />
      )}
      <MOfferRibbon label={ribbon} />
      {/* videos and embeds bring their own playback — the static play affordance
          only makes sense over a still */}
      {(!media || media.kind === "image") && (
        <Flex
          position="absolute"
          inset="0"
          align="center"
          justify="center"
          pointerEvents="none"
        >
          <MOfferPlayButton />
        </Flex>
      )}
    </Box>

    {/* contentWrapper: the one-line title (truncates, never wraps) */}
    <Box pt={s(10.65)} px={s(10.65)} pl={s(15.56)} pb={s(8.52)} minW="0">
      <Text color="black" fontWeight="700" fontSize={s(21.3)} noOfLines={1}>
        {title ?? game?.name ?? "App name"}
      </Text>
    </Box>

    {/* CTA: deviceWidth/1.3 wide (84.6% of the card), the #a10b6f slab peeking
        6px below it, and the pulsing Ring behind both (ButtonBackground +
        Ring/index.tsx) */}
    <Flex
      position="relative"
      w="84.6%"
      h={s(60.86)}
      mx="auto"
      align="center"
      justify="center"
    >
      <Box
        position="absolute"
        left="-9.05%"
        top="-37.5%"
        w="118.1%"
        h="175%"
        borderRadius="40px"
        bg="#ff7d44"
        pointerEvents="none"
        sx={{
          // the app's opacity is 2 - 2 * progress, so it holds at full for the
          // first half of the sweep and fades out over the second.
          // `backwards` matters: without it the element sits at its own
          // scale(1)/opacity(1) — a full orange slab over the card — for the
          // 1.2s before the first pulse starts.
          animation: "offerRing 2.2s linear 1.2s infinite backwards",
          "@keyframes offerRing": {
            "0%": { transform: "scale(0)", opacity: 1 },
            "50%": { transform: "scale(0.505)", opacity: 1 },
            "100%": { transform: "scale(1.01)", opacity: 0 },
          },
        }}
      />
      <Box position="absolute" inset="0" top={s(6)} borderRadius={s(12)} bg="#a10b6f" />
      <Box
        position="absolute"
        inset="0"
        borderRadius={s(12)}
        background="linear-gradient(to left, #ff7d44 0%, #ff4f4f 60%, #fc0fa5 100%)"
      />
      <Flex position="relative" align="center" justify="center">
        <Text
          color="white"
          fontFamily={TM.fontAlt}
          fontWeight="800"
          // localized CTAs run long ("SPIELEN & VERDIENEN") — step the size
          // down so the label stays inside the pill instead of overflowing
          fontSize={s(cta.length > 12 ? 16.5 : 22.42)}
          letterSpacing="1px"
          position="relative"
          top={s(-2.43)}
          textShadow="1px 1px 4px rgb(165,0,255)"
          whiteSpace="nowrap"
        >
          {cta}
        </Text>
        <Box
          position="relative"
          left={s(8.52)}
          top={s(-1.7)}
          borderRadius="full"
          border="0.8px solid white"
          flexShrink={0}
        >
          <MGameIcon game={game} size={s(42.6)} radius="full" />
          <Flex
            position="absolute"
            bottom="0"
            right={s(-4.26)}
            w={s(18.52)}
            h={s(18.52)}
            borderRadius="full"
            bg="white"
            align="center"
            justify="center"
          >
            <PlayStoreIcon size={s(12.17)} />
          </Flex>
        </Box>
      </Flex>
    </Flex>
  </Box>
);

export const MPlayCircle: React.FC = () => (
  <Flex
    w="38.73px"
    h="38.73px"
    borderRadius="full"
    bg={TM.green}
    align="center"
    justify="center"
    boxShadow="0 2px 6px rgba(34,197,94,0.35)"
    flexShrink={0}
  >
    <Icon as={MdPlayArrow} color="white" w="21.3px" h="21.3px" ml="2px" />
  </Flex>
);
